import React, { useState, useEffect } from 'react';
import { ArrowRight, Plane, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const heroImages = [
  'https://images.pexels.com/photos/5778470/pexels-photo-5778470.jpeg',
  'https://customer-assets.emergentagent.com/job_izi-travels-preview/artifacts/v9cxj8xj_HILTON%20KIN.jpg',
  'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1920&q=80',
  'https://customer-assets.emergentagent.com/job_izi-travels-preview/artifacts/98snshui_limeteKin.jpg',
  'https://images.unsplash.com/photo-1530521954074-e64f6810b32d?w=1920&q=80',
  'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1920&q=80'
];

const Hero = () => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const goToSlide = (idx) => setCurrentSlide(idx);
  const goPrev = () => setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  const goNext = () => setCurrentSlide((prev) => (prev + 1) % heroImages.length);

  const scrollToReservation = () => {
    const element = document.getElementById('reservation');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="accueil" className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Background Carousel */}
      <div className="absolute inset-0 z-0">
        {heroImages.map((img, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              idx === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${img})` }}
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/80 to-blue-900/30" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center lg:text-left">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 rounded-full mb-8 animate-fade-in">
            <Plane className="text-white" size={20} />
            <span className="text-white font-medium">{t('premiumAgency')}</span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight animate-slide-up">
            {t('heroTitle')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-cyan-300">
              IZI Travels
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-blue-100 mb-10 leading-relaxed animate-slide-up-delay">
            {t('heroSubtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 animate-slide-up-delay-2">
            <button
              onClick={scrollToReservation}
              className="group bg-white text-blue-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all duration-300 shadow-2xl hover:scale-105 flex items-center justify-center gap-2"
            >
              {t('bookNow')}
              <ArrowRight className="group-hover:translate-x-1 transition-transform duration-300" size={20} />
            </button>
            <button
              onClick={() => document.getElementById('destinations').scrollIntoView({ behavior: 'smooth' })}
              className="bg-blue-600/20 backdrop-blur-md text-white border-2 border-white/30 px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-600/30 hover:border-white/50 transition-all duration-300 shadow-xl"
            >
              {t('discoverDestinations')}
            </button>
          </div>
        </div>
      </div>

      {/* Carousel Controls */}
      <button
        onClick={goPrev}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full items-center justify-center text-white transition-all duration-300 hover:scale-110"
        aria-label="Image précédente"
        data-testid="hero-prev-btn"
      >
        <ChevronLeft size={28} />
      </button>
      <button
        onClick={goNext}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full items-center justify-center text-white transition-all duration-300 hover:scale-110"
        aria-label="Image suivante"
        data-testid="hero-next-btn"
      >
        <ChevronRight size={28} />
      </button>

      {/* Slide indicators + Scroll Indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-4">
        <div className="flex gap-2">
          {heroImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goToSlide(idx)}
              className={`h-2 rounded-full transition-all duration-300 ${
                idx === currentSlide ? 'w-10 bg-white' : 'w-2 bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Aller à l'image ${idx + 1}`}
              data-testid={`hero-indicator-${idx}`}
            />
          ))}
        </div>
        <div className="hidden sm:block w-px h-5 bg-white/30" />
        <div className="hidden sm:block animate-bounce" aria-hidden="true">
          <div className="w-5 h-8 border-2 border-white/60 rounded-full flex items-start justify-center p-1">
            <div className="w-1 h-2 bg-white rounded-full animate-scroll" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
